import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilePdf, ShieldCheck, User, Calendar, Warning, ArrowRight } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/SignaturePad";

const SECTION_LABEL = {
  general: "Reporte General de RRHH",
  nomina: "Nómina",
  calendar: "Calendario de Roles",
  certs: "Certificados",
  vacancies: "Vacantes",
  reimbursements: "Reembolsos",
};

/** Página móvil-friendly para que jefatura firme la validación de un PDF de HR
 *  desde el celular escaneando el QR impreso al pie del reporte. */
export default function HrValidate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { user } = useAuth();

  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signature, setSignature] = useState("");
  const [note, setNote] = useState("");
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/hr/pdf-signatures/${encodeURIComponent(token)}`);
      setMeta(data);
      if (data.signer_name) setSignerName(data.signer_name);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.name && !signerName) setSignerName(user.name); }, [user, signerName]);

  // If not logged in, kick to login and come back here
  useEffect(() => {
    if (user === false) {
      nav(`/login?next=${encodeURIComponent(`/hr-validate?token=${token}`)}`, { replace: true });
    }
  }, [user, nav, token]);

  const submit = async () => {
    if (!signature) { toast.error("Firma vacía — dibuja tu firma para continuar"); return; }
    setBusy(true);
    try {
      await api.post(`/hr/pdf-signatures/${encodeURIComponent(token)}/sign`, {
        signature_data_url: signature,
        note,
        signer_name: signerName,
      });
      toast.success("Reporte firmado y validado ✔");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          <Warning size={40} weight="duotone" className="text-amber-600 mx-auto mb-2" />
          <h1 className="font-heading font-black text-xl text-slate-900">Token faltante</h1>
          <p className="text-sm text-slate-500 mt-2">
            Escanea el código QR impreso al pie del reporte para acceder a esta pantalla.
          </p>
        </div>
      </div>
    );
  }

  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  }

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          <Warning size={40} weight="duotone" className="text-red-600 mx-auto mb-2" />
          <h1 className="font-heading font-black text-xl text-slate-900">Token inválido</h1>
          <p className="text-sm text-slate-500 mt-2">
            El código escaneado no corresponde a ningún reporte activo. Solicita a RRHH que regenere el PDF.
          </p>
        </div>
      </div>
    );
  }

  const alreadySigned = meta.status === "signed";
  const sectionLbl = SECTION_LABEL[meta.section] || meta.section;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 py-6 px-3">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-4" data-testid="hr-validate-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <ShieldCheck size={22} weight="duotone" className="text-blue-900" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Validación de reporte HR</p>
              <h1 className="font-heading font-black text-lg text-slate-900 leading-tight">{sectionLbl}</h1>
            </div>
          </div>

          <div className="space-y-2 text-sm text-slate-700 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate-400" />
              <span className="text-xs text-slate-500 w-24">Fecha reporte:</span>
              <span className="font-semibold" data-testid="hr-validate-date">{meta.report_date}</span>
            </div>
            <div className="flex items-center gap-2">
              <User size={14} className="text-slate-400" />
              <span className="text-xs text-slate-500 w-24">Generado por:</span>
              <span className="font-semibold">{meta.generated_by || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <FilePdf size={14} className="text-slate-400" />
              <span className="text-xs text-slate-500 w-24">Token:</span>
              <span className="font-mono text-xs">{meta.token}</span>
            </div>
          </div>
        </div>

        {alreadySigned ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center" data-testid="hr-validate-signed">
            <ShieldCheck size={40} weight="duotone" className="text-emerald-600 mx-auto mb-2" />
            <h2 className="font-heading font-black text-lg text-emerald-900">Reporte ya validado ✔</h2>
            <p className="text-sm text-emerald-800 mt-1">
              Firmado por <b>{meta.signer_name}</b> el {meta.signed_at?.slice(0, 16).replace("T", " ")}.
            </p>
            {meta.note && (
              <p className="text-xs text-emerald-700 italic mt-2">&ldquo;{meta.note}&rdquo;</p>
            )}
            {meta.signature_data_url && (
              <img src={meta.signature_data_url} alt="firma"
                   className="mx-auto mt-3 h-24 bg-white rounded border border-emerald-200 p-1"
                   data-testid="hr-validate-signature-img" />
            )}
            <Button variant="outline" className="mt-4 gap-1.5" onClick={() => nav("/hr")}>
              Ir al módulo de RRHH <ArrowRight size={14} weight="bold" />
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5" data-testid="hr-validate-form">
            <div className="space-y-3">
              <div>
                <Label className="overline-label">Nombre que firma</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                       placeholder="Jefatura de RRHH"
                       data-testid="hr-validate-signer-name" />
              </div>
              <div>
                <Label className="overline-label">Firma manuscrita</Label>
                <SignaturePad value={signature} onChange={setSignature} width={480} height={140}
                              testid="hr-validate-signature" />
              </div>
              <div>
                <Label className="overline-label">Nota (opcional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Observaciones sobre este reporte…"
                          data-testid="hr-validate-note" />
              </div>
              <Button onClick={submit} disabled={busy || !signature}
                      className="w-full bg-blue-900 hover:bg-blue-800 gap-1.5"
                      data-testid="hr-validate-submit">
                <ShieldCheck size={16} weight="bold" /> {busy ? "Firmando…" : "Firmar y validar reporte"}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Al firmar, se guardará tu nombre, correo autenticado, fecha y hora en el sistema.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
